import { ReactNode, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { SlipContext } from 'context/slipContext';
import { UserContext } from 'context/userContext';
import { formatBalanceDisplay } from 'helpers/balanceFormat';
import { VipIcon, VipIconName } from './VipIcons';
import styles from './VipChrome.module.css';

/** The bar over every VIP page; the statistics page uses it too. */
export const VipHeader = ({
  title,
  hideBack,
  onBack,
  trailing,
  onMenu,
}: {
  title: ReactNode;
  hideBack?: boolean;
  /** Leave out to go one page back. */
  onBack?: () => void;
  trailing?: ReactNode;
  onMenu?: () => void;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div className={styles.header} data-vip-dark>
      {hideBack ? (
        <span className={styles.roundGap} />
      ) : (
        <VipRoundButton
          label={t('previous_label')}
          onClick={onBack ?? (() => navigate(-1))}
        >
          <VipIcon name="back" />
        </VipRoundButton>
      )}
      <span className={styles.title}>{title}</span>
      {trailing}
      {onMenu ? (
        <VipRoundButton label={t('other_label')} onClick={onMenu}>
          <VipIcon name="menu" />
        </VipRoundButton>
      ) : (
        <span className={styles.roundGap} />
      )}
    </div>
  );
};

/** A round button of the header, for a page's own (search) too. */
export const VipRoundButton = ({
  label,
  onClick,
  on,
  children,
}: {
  label: string;
  onClick: () => void;
  on?: boolean;
  children: ReactNode;
}) => (
  <button
    type="button"
    className={classNames(styles.round, on && styles.roundOn)}
    aria-label={label}
    aria-pressed={on}
    onClick={onClick}
  >
    {children}
  </button>
);

/** Home, sports, the slip in the middle (picks and total odds on it), then
 *  my bets and the account with the balance; a guest gets live and login. */
export const VipFooter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const url = useLocation().pathname;
  const { loggedUser } = useContext(UserContext);
  const { maxCoefficient, getTotalBets } = useContext(SlipContext);
  const picks = getTotalBets();

  const item = (
    to: string,
    icon: VipIconName,
    label: ReactNode,
    active = url === to
  ) => (
    <button
      type="button"
      className={classNames(styles.item, active && styles.itemOn)}
      aria-current={active ? 'page' : undefined}
      onClick={() => navigate(to)}
    >
      <VipIcon name={icon} size={22} />
      {label}
    </button>
  );

  return (
    <nav className={styles.footer} data-vip-dark>
      {item('/', 'home', <b>{t('home_label')}</b>)}
      {item(
        '/sports-betting',
        'ball',
        <b>{t('sports_betting_label')}</b>,
        url.startsWith('/sports-betting')
      )}
      <button
        type="button"
        className={styles.slip}
        aria-label={t('slip_label')}
        aria-current={url === '/bet-slip' ? 'page' : undefined}
        onClick={() => navigate('/bet-slip')}
      >
        <span className={styles.slipRound}>
          <VipIcon name="ticket" size={24} />
          {picks > 0 && <em>{picks}</em>}
        </span>
        <b>{maxCoefficient.toFixed(2)}</b>
      </button>
      {loggedUser ? (
        <>
          {item('/my-bets', 'list', <b>{t('footer_bets_label')}</b>)}
          {item(
            '/account-settings',
            'user',
            <b className={styles.balance}>
              {formatBalanceDisplay(loggedUser.balance ?? 0)}
            </b>
          )}
        </>
      ) : (
        <>
          {item('/live-betting', 'live', <b>{t('footer_live_label')}</b>)}
          {item('/login', 'login', <b>{t('login_label')}</b>)}
        </>
      )}
    </nav>
  );
};
